import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { leaveTeam as leaveTeamApi } from "../api/members";
import { useHostToast } from "../../host/useHostToast";
import { useTrack } from "../../host/useTrack";
import { useCurrentUser } from "../../host/useCurrentUser";
import { getHost } from "../../host/getHost";

const MY_TEAM = "My Team";

interface UseLeaveTeam {
  pending: boolean;
  leave: () => Promise<boolean>;
}

// Where the presenter's router lands after a successful leave. Matches
// `this.$router.push({ name: 'Presentations' })` in the original
// LeaveTeam.vue — the user no longer has a team, so the My Team page
// would be invalid for them.
const POST_LEAVE_PATH = "/apps/presentations";

export function useLeaveTeam(): UseLeaveTeam {
  const { t } = useTranslation();
  const toast = useHostToast();
  const track = useTrack();
  const currentUser = useCurrentUser();
  const [pending, setPending] = useState(false);

  const leave = useCallback(async () => {
    setPending(true);
    try {
      await leaveTeamApi();
      // Mirrors LeaveTeam.vue's success-path tracking. `isAdmin: false`
      // is what the original sets — the file has separate Admin/Member
      // copy but the same payload shape; we don't differentiate yet.
      track("Team Management > Leave team > Confirmed", {
        eventCategory: MY_TEAM,
        eventLabel: currentUser?.email,
        plan: currentUser?.planType,
        isAdmin: false,
      });
      toast.success(t("team.toast.leftTeam"));
      // Push the host's outer router to /apps/presentations — same as
      // the original Vue flow. The iframe unmounts as the parent leaves
      // the My Team route. If the bridge is missing (standalone dev or
      // host without onRouteChange), the API call still succeeded; the
      // user's just stuck on a stale view until they navigate manually.
      getHost()?.onRouteChange?.({
        location: POST_LEAVE_PATH,
        navType: "push",
        fullScreen: false,
      });
      return true;
    } catch {
      toast.error(t("team.toast.genericError"));
      return false;
    } finally {
      setPending(false);
    }
  }, [t, toast, track, currentUser?.email, currentUser?.planType]);

  return { leave, pending };
}
